import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { isNodeSelection } from '@tiptap/core';
import { usePortalNode } from '@wix/da-ds/pkg/popper/hooks/usePortalNode';
import throttle from 'lodash/throttle';
import ddt from '@wix/da-ddt';
import cn from 'classnames';
import { useSelector } from 'react-redux';
import { getMenuUpdate } from '../../../redux/selectors/editor';
import s from './BlockBubbleMenu.scss';
const ContainerPortal = ({ children })=>{
    const portalContainer = usePortalNode('bubblemenublock');
    return portalContainer && /*#__PURE__*/ ReactDOM.createPortal(children, portalContainer);
};
/**
 *
 */ export const BlockBubbleMenu = ({ editor, open, className, children, scrollContainerRef, menuOffset = 8, menuHeight, menuWidth, borderPadding = 8, childSelector })=>{
    const menuUp = useSelector(getMenuUpdate); // used for externals to trigger updates
    const [winScrollPos, setWinScrollPos] = useState({
        y: window.scrollY,
        x: window.scrollX
    });
    const [innerScrollPos, setInnerScrollPos] = useState({
        y: 0,
        x: 0
    });
    const [menuStyles, setMenuStyles] = useState({});
    // Track the window position
    useEffect(()=>{
        const scroller = window;
        const handleScroll = throttle(()=>{
            setWinScrollPos({
                y: scroller === null || scroller === void 0 ? void 0 : scroller.scrollY,
                x: scroller === null || scroller === void 0 ? void 0 : scroller.scrollX
            });
        }, 200);
        if (scroller) {
            scroller.addEventListener('scroll', handleScroll, {
                passive: true
            });
        }
        return ()=>scroller === null || scroller === void 0 ? void 0 : scroller.removeEventListener('scroll', handleScroll);
    }, [
        open
    ]);
    // Force updates when a non page scroller scrolls, we need to update
    // the menu position on scroll / we don't actually use the value.
    useEffect(()=>{
        const scroller = scrollContainerRef === null || scrollContainerRef === void 0 ? void 0 : scrollContainerRef.current;
        const handleScroll = throttle(()=>{
            setInnerScrollPos({
                y: scroller === null || scroller === void 0 ? void 0 : scroller.scrollTop,
                x: scroller === null || scroller === void 0 ? void 0 : scroller.scrollLeft
            });
        }, 200);
        if (scroller) {
            scroller.addEventListener('scroll', handleScroll, {
                passive: true
            });
        }
        return ()=>scroller === null || scroller === void 0 ? void 0 : scroller.removeEventListener('scroll', handleScroll);
    }, [
        scrollContainerRef,
        open
    ]);
    useEffect(()=>{
        if (open) {
            // RAF because sometimes the DOM isn't ready in the same render
            requestAnimationFrame(()=>{
                const { ranges } = editor.state.selection;
                const from = Math.min(...ranges.map((range)=>range.$from.pos));
                let rect;
                // Block is selected only
                if (isNodeSelection(editor.state.selection)) {
                    const node = editor.view.nodeDOM(from);
                    if (node instanceof HTMLElement) {
                        if (childSelector) {
                            const child = node.querySelector(childSelector);
                            if (child) {
                                rect = child.getBoundingClientRect();
                            }
                        } else {
                            rect = node.getBoundingClientRect();
                        }
                    }
                }
                if (rect) {
                    var _visualViewport, _scrollContainerRef_current;
                    let newTop = rect.top + winScrollPos.y - menuOffset - menuHeight;
                    let newLeft = rect.left + winScrollPos.x + rect.width / 2 - menuWidth / 2;
                    let newRight = undefined;
                    var _visualViewport_width;
                    // off right of screen we move it back inside
                    if (newLeft + menuWidth > ((_visualViewport_width = (_visualViewport = visualViewport) === null || _visualViewport === void 0 ? void 0 : _visualViewport.width) !== null && _visualViewport_width !== void 0 ? _visualViewport_width : Infinity)) {
                        newLeft = 'auto';
                        newRight = `${borderPadding}px`;
                    }
                    // off top of scrollarea
                    // With ref we keep within the scrollable not the screen
                    const scrollableRefRect = scrollContainerRef === null || scrollContainerRef === void 0 ? void 0 : (_scrollContainerRef_current = scrollContainerRef.current) === null || _scrollContainerRef_current === void 0 ? void 0 : _scrollContainerRef_current.getBoundingClientRect();
                    if (scrollableRefRect) {
                        // the selection rect top is outside the scrollarea and will be floating
                        // in the page, we bring it back into the scrollable area and account for
                        // viewport scroll pos.
                        if (rect.top - menuHeight < scrollableRefRect.top) {
                            newTop = scrollableRefRect.top + winScrollPos.y + borderPadding;
                        }
                        // Also keep the menu inside the bottom of the scrollable
                        if (newTop > scrollableRefRect.bottom + winScrollPos.y) {
                            newTop = scrollableRefRect.bottom - (menuHeight + borderPadding) + winScrollPos.y;
                        }
                    }
                    // Without ref, we keep within the screen
                    if (!(scrollContainerRef === null || scrollContainerRef === void 0 ? void 0 : scrollContainerRef.current)) {
                        if (rect.top - menuHeight < 0) {
                            newTop = winScrollPos.y + borderPadding;
                        }
                    }
                    const calcStyles = {
                        top: `${newTop}px`,
                        left: `${newLeft}px`,
                        position: 'absolute',
                        right: newRight
                    };
                    ddt.log('editormenu', 'Menu Position', {
                        calcStyles,
                        rect,
                        scrollableRefRect,
                        scrollContainerRef,
                        winScrollPos
                    });
                    setMenuStyles(calcStyles);
                // Left here because it can be useful to visualise the rect :)
                /* const debugRect = document.getElementById('debug-rect');
          const debug = debugRect ?? document.createElement('div');
          if (!debugRect) {
            document.body.appendChild(debug);
          }

          const styles: React.CSSProperties = {
            position: 'absolute',
            width: `${rect.width}px`,
            height: `${rect.height}px`,
            left: `${rect.left + window.scrollX}px`,
            top: `${rect.top + window.scrollY}px`,
          };

          debug.setAttribute('id', 'debug-rect');
          debug.style.position = 'absolute';
          debug.style.backgroundColor = '#ff0000aa';
          debug.style.width = `${rect.width}px`;
          debug.style.height = `${rect.height}px`;
          debug.style.left = styles.left as any;
          debug.style.top = styles.top as any;
          debug.style.zIndex = '999999';
          debug.style.minWidth = '10px';
          debug.style.pointerEvents = 'none';

          // Left here because it can be useful to visualise the rect :)
          const debugRect2 = document.getElementById('debug-rect2');
          const debug2 = debugRect2 ?? document.createElement('div');
          if (!debugRect2) {
            document.body.appendChild(debug2);
          }

          const styles2: React.CSSProperties = {
            position: 'absolute',
            width: `${scrollableRefRect?.width}px`,
            height: `${scrollableRefRect?.height}px`,
            left: `${(scrollableRefRect?.left ?? 0) + window.scrollX}px`,
            top: `${(scrollableRefRect?.top ?? 0) + winScrollPos.y}px`,
          };

          debug2.setAttribute('id', 'debug-rect2');
          debug2.style.position = 'absolute';
          debug2.style.backgroundColor = '#00ff00aa';
          debug2.style.width = `${scrollableRefRect?.width}px`;
          debug2.style.height = `${scrollableRefRect?.height}px`;
          debug2.style.left = styles2.left as any;
          debug2.style.top = styles2.top as any;
          debug2.style.zIndex = '999999';
          debug2.style.minWidth = '10px';
          debug2.style.pointerEvents = 'none';*/ }
            });
        }
    }, [
        editor.state.selection,
        editor.view,
        open,
        winScrollPos,
        innerScrollPos,
        menuOffset,
        menuHeight,
        scrollContainerRef,
        menuWidth,
        borderPadding,
        childSelector,
        menuUp
    ]);
    if (!open) {
        return null;
    }
    return /*#__PURE__*/ React.createElement(ContainerPortal, null, /*#__PURE__*/ React.createElement("div", {
        className: cn(s['wrapper'], className),
        style: menuStyles
    }, children));
};
BlockBubbleMenu.displayName = 'BlockBubbleMenu';
export default BlockBubbleMenu;

//# sourceMappingURL=BlockBubbleMenu.js.map